import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Check, 
  X, 
  ArrowLeft, 
  Crown, 
  Loader2, 
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBSCRIPTION_TIERS = [
  {
    id: 'free',
    name: 'Hobbyist',
    price: 0,
    description: 'For casual users interested in local awareness',
    features: [
      { text: '6 hours of incident history', included: true },
      { text: 'Basic incident filters', included: true },
      { text: 'Map view', included: true },
      { text: 'Email alerts', included: false },
      { text: 'Geofence zones', included: false },
      { text: 'Advanced filters', included: false },
      { text: 'API access', included: false },
    ],
    popular: false,
    cta: 'Current Plan'
  },
  {
    id: 'amateur',
    name: 'Amateur',
    price: 9.99,
    description: 'For homeowners who want monitoring coverage',
    features: [
      { text: '24 hours of incident history', included: true },
      { text: 'All incident filters', included: true },
      { text: 'Map view', included: true },
      { text: 'Email alerts', included: true },
      { text: '2 geofence zones', included: true },
      { text: 'Advanced filters', included: false },
      { text: 'API access', included: false },
    ],
    popular: false,
    cta: 'Upgrade'
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 29.99,
    description: 'For security professionals & journalists',
    features: [
      { text: '7 days of incident history', included: true },
      { text: 'All incident filters', included: true },
      { text: 'Map view with heatmaps', included: true },
      { text: 'Email + SMS alerts', included: true },
      { text: '10 geofence zones', included: true },
      { text: 'Advanced filters', included: true },
      { text: 'API access', included: false },
    ],
    popular: true,
    cta: 'Upgrade'
  },
  {
    id: 'business',
    name: 'Business',
    price: 99.99,
    description: 'For organizations needing full data access',
    features: [
      { text: '30 days of incident history', included: true },
      { text: 'All incident filters', included: true },
      { text: 'Map view with heatmaps', included: true },
      { text: 'All notification channels', included: true },
      { text: 'Unlimited geofence zones', included: true },
      { text: 'Advanced filters + Export', included: true },
      { text: 'Full API access', included: true },
    ],
    popular: false,
    cta: 'Upgrade'
  }
];

const PricingCard = ({ tier, currentTier, onSelect, loading }) => {
  const isCurrent = tier.id === currentTier;
  const isUpgrade = SUBSCRIPTION_TIERS.findIndex(t => t.id === tier.id) > 
                    SUBSCRIPTION_TIERS.findIndex(t => t.id === currentTier);

  return (
    <Card 
      className={`relative flex flex-col glass-card border ${
        tier.popular 
          ? 'border-teal-500/50 shadow-lg shadow-teal-500/10' 
          : 'border-slate-800'
      } ${isCurrent ? 'ring-2 ring-teal-500' : ''}`}
      data-testid={`pricing-card-${tier.id}`}
    >
      {tier.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-teal-600 text-white px-3">Most Popular</Badge>
        </div>
      )}
      
      {isCurrent && (
        <div className="absolute -top-3 right-4">
          <Badge className="bg-slate-600 text-white px-3">Current</Badge>
        </div>
      )}

      <CardHeader className="text-center pb-2">
        <CardTitle className="text-xl font-bold text-slate-100">
          {tier.name}
        </CardTitle>
        <div className="mt-2">
          <span className="text-4xl font-bold text-slate-100">
            ${tier.price}
          </span>
          <span className="text-slate-500">/month</span>
        </div>
        <CardDescription className="text-slate-400 mt-2">
          {tier.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1">
        <ul className="space-y-3">
          {tier.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2">
              {feature.included ? (
                <Check className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
              ) : (
                <X className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
              )}
              <span className={feature.included ? 'text-slate-300' : 'text-slate-600'}>
                {feature.text}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>

      <CardFooter>
        {isCurrent ? (
          <Button 
            disabled 
            className="w-full bg-slate-700 text-slate-400"
          >
            Current Plan
          </Button>
        ) : tier.id === 'free' ? (
          <Button 
            disabled 
            variant="outline"
            className="w-full border-slate-700 text-slate-400"
          >
            Free Plan
          </Button>
        ) : isUpgrade ? (
          <Button 
            onClick={() => onSelect(tier.id)}
            disabled={loading}
            className="w-full bg-teal-600 hover:bg-teal-500 text-white glow-teal"
            data-testid={`upgrade-btn-${tier.id}`}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Upgrade to {tier.name}
          </Button>
        ) : (
          <Button 
            disabled
            variant="outline"
            className="w-full border-slate-700 text-slate-500"
          >
            Downgrade
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

const SubscriptionPage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState('');

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Payment verification timed out. Please check your email for confirmation.' });
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(
        `${API_URL}/api/subscriptions/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setPaymentStatus({ 
          status: 'success', 
          message: `Successfully upgraded to ${response.data.tier}!`,
          tier: response.data.tier
        });
        await refreshUser();
        setCheckingPayment(false);
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus({ status: 'expired', message: 'Payment session expired. Please try again.' });
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment:', err);
      setPaymentStatus({ status: 'error', message: 'Error checking payment status.' });
      setCheckingPayment(false);
    }
  }, [token, refreshUser]);

  // Check for payment success
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && token) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, token, pollPaymentStatus]);

  const handleSelectTier = async (tierId) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_URL}/api/subscriptions/checkout`,
        { 
          tier: tierId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950" data-testid="subscription-page">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-radial from-slate-900 to-slate-950" />
      
      {/* Header */}
      <header className="relative z-10 glass-card border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-slate-200 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </Link>
          
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_presence-check-26/artifacts/zsrnp3ib_veriwatch_dark.png"
              alt="VeriWatch"
              className="h-8 w-auto"
            />
          </Link>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 py-12">
        {/* Payment status alerts */}
        {checkingPayment && (
          <Alert className="mb-8 bg-blue-950/50 border-blue-800">
            <Clock className="h-4 w-4 text-blue-400" />
            <AlertDescription className="text-blue-300">
              Verifying your payment... Please wait.
            </AlertDescription>
          </Alert>
        )}

        {paymentStatus?.status === 'success' && (
          <Alert className="mb-8 bg-green-950/50 border-green-800">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <AlertDescription className="text-green-300">
              {paymentStatus.message}
            </AlertDescription>
          </Alert>
        )}

        {paymentStatus?.status === 'error' && (
          <Alert className="mb-8 bg-red-950/50 border-red-800">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <AlertDescription className="text-red-300">
              {paymentStatus.message}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert className="mb-8 bg-red-950/50 border-red-800">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <AlertDescription className="text-red-300">{error}</AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-950/50 border border-teal-800/50 mb-4">
            <Crown className="w-4 h-4 text-teal-400" />
            <span className="text-sm text-teal-300">Choose Your Plan</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-100 uppercase">
            Subscription Plans
          </h1>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
            Unlock more history, advanced filters, and premium features with our paid plans
          </p>
        </div>

        {/* Pricing grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {SUBSCRIPTION_TIERS.map((tier) => (
            <PricingCard
              key={tier.id}
              tier={tier}
              currentTier={user?.tier || 'free'}
              onSelect={handleSelectTier}
              loading={loading}
            />
          ))}
        </div>

        {/* Features comparison note */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-500">
            All plans include access to real-time incident monitoring and map view.
            <br />
            Need enterprise features? <a href="mailto:contact@veriwatch.com" className="text-teal-400 hover:underline">Contact us</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPage;
